import {
  invalidatePoolLoads,
  recommendationPoolKey,
} from "@/library/recommendation-pool";
import { nicknameKey } from "@/storage/nickname";
import { readerRange, type ReaderDb } from "@/storage/db";
import { enrollmentKey } from "@/storage/enrollment";
import { forgetStrayWordArms } from "@/storage/stray-word-arms";
import { fluencyWatermarkKey } from "./fluency";
import { offlineReader, type OfflineReader } from "./offline-reader";
import { readingSessionKey } from "./reading-session";

/**
 * Deletes what this device shows of one reader: the bookmark and fluency
 * mirrors, the personalised readings served to them and their enrollment
 * mark, any study arms an older build's tab stored for them, the fluency
 * watermark, the cached recommendation pool, the nickname, the reading
 * session and the
 * device's offline reader when it names them. Every other reader's
 * rows stay, since a classroom device is shared.
 *
 * The outbox and quarantine stay too: they hold research data that reader
 * produced on this device before, which flushes only under that reader's
 * own session, so nobody else can read it back. So does the mirror row of
 * a bookmark still pending in the outbox: it is that reader's unsent place,
 * and a server merge keeps only a mirror row that exists.
 */
export async function forgetReader(
  db: ReaderDb,
  readerId: string,
  storage: Pick<Storage, "removeItem"> = localStorage,
  offline: Pick<OfflineReader, "forget"> = offlineReader,
  onStrayArmsFailed: (error: unknown) => void = () => undefined,
): Promise<void> {
  invalidatePoolLoads(readerId);
  const [lower, upper] = readerRange(readerId);
  await db.transaction(
    "rw",
    [
      db.fluency,
      db.bookmarks,
      db.outboxBookmarks,
      db.personalReadings,
      db.meta,
    ],
    async () => {
      const pending = new Set(
        (
          await db.outboxBookmarks
            .where("[readerId+readingId]")
            .between(lower, upper)
            .filter((row) => row.state === "pending")
            .toArray()
        ).map((row) => row.readingId),
      );
      await Promise.all([
        db.fluency.where("[readerId+word]").between(lower, upper).delete(),
        db.personalReadings
          .where("[readerId+readingId]")
          .between(lower, upper)
          .delete(),
        db.bookmarks
          .where("[readerId+readingId]")
          .between(lower, upper)
          .filter((row) => !pending.has(row.readingId))
          .delete(),
        db.meta.delete(fluencyWatermarkKey(readerId)),
        db.meta.delete(enrollmentKey(readerId)),
        db.meta.delete(recommendationPoolKey(readerId)),
      ]);
    },
  );
  storage.removeItem(nicknameKey(readerId));
  storage.removeItem(readingSessionKey(readerId));
  offline.forget(readerId);
  await forgetStrayWordArms(db, readerId).catch(onStrayArmsFailed);
}
